import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Shield, Plus, Upload, Download, Search, Trash2, Eye, 
  ChevronDown, X, Calendar, Users, Building2, AlertTriangle,
  CheckCircle, FileSpreadsheet, Pencil
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RISK_COLORS = {
  verde: 'bg-green-500',
  amarillo: 'bg-yellow-400',
  naranja: 'bg-orange-500',
  rojo: 'bg-red-600',
};

function RiskBadge({ value, color }) {
  if (!value) return <span className="text-slate-300">-</span>;
  const bg = RISK_COLORS[color] || 'bg-slate-300';
  return (
    <span className={`${bg} text-white text-xs font-bold px-2 py-0.5 rounded inline-block min-w-[28px] text-center`}>
      {value}
    </span>
  );
}

function PersonSearch({ onSelect, placeholder = "Buscar persona..." }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/personas?search=${query}&per_page=10`);
        const list = Array.isArray(data) ? data : data.personas || [];
        setResults(list);
        setOpen(true);
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <div className="flex items-center border rounded-lg px-3 py-2 bg-white">
        <Search className="w-4 h-4 text-slate-400 mr-2" />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={placeholder}
          className="flex-1 outline-none text-sm" data-testid="person-search-input" />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {results.map((p) => (
            <button key={p.id} data-testid={`person-result-${p.id}`}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm border-b last:border-0"
              onClick={() => { onSelect(p); setQuery(''); setOpen(false); }}>
              <span className="font-medium">{p.nombre} {p.apellido}</span>
              <span className="text-slate-400 ml-2 text-xs">{p.cargo_nombre || ''}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ValidationModal({ warnings, onForce, onCancel }) {
  return createPortal(
    <div style={{backgroundColor:"rgba(0,0,0,0.5)"}} className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6" data-testid="validation-modal">
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle className="w-6 h-6 text-amber-500" />
          <h3 className="text-lg font-bold text-slate-900">Valores no coinciden</h3>
        </div>
        <p className="text-sm text-slate-600 mb-4">Los siguientes valores del Excel no coinciden con la plataforma:</p>
        <div className="space-y-3 max-h-60 overflow-y-auto">
          {warnings.map((w, i) => (
            <div key={i} className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm font-medium text-amber-800 capitalize">{w.campo}</p>
              <p className="text-sm text-slate-700">Valor Excel: <strong>{w.valor_excel}</strong></p>
              <p className="text-xs text-slate-500 mt-1">Sugerencias: {w.sugerencias.slice(0, 5).join(', ')}</p>
            </div>
          ))}
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onCancel} className="flex-1 px-4 py-2 border rounded-lg text-sm hover:bg-slate-50" data-testid="modal-cancel">
            Cancelar y corregir
          </button>
          <button onClick={onForce} className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm hover:bg-amber-600" data-testid="modal-force">
            Importar de todas formas
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function CreateQRAForm({ onCreated, onCancel }) {
  const [gerencias, setGerencias] = useState([]);
  const [supers, setSupers] = useState([]);
  const [areas, setAreas] = useState([]);
  const [form, setForm] = useState({ codigo: '', nombre: '', gerencia_id: '', superintendencia_id: '', area_id: '', 
    fecha_evaluacion: '', aprobador_nombre: '', aprobador_cargo: '' });
  const [evaluadores, setEvaluadores] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/gerencias`),
      axios.get(`${API}/superintendencias`),
      axios.get(`${API}/areas`),
    ]).then(([g, s, a]) => {
      setGerencias(g.data);
      setSupers(s.data);
      setAreas(a.data);
    });
  }, []);

  const filteredSupers = form.gerencia_id ? supers.filter((s) => s.gerencia_id === form.gerencia_id) : supers;

  const addEvaluador = (persona) => {
    if (evaluadores.find((e) => e.persona_id === persona.id)) return;
    setEvaluadores([...evaluadores, { persona_id: persona.id, nombre: `${persona.nombre} ${persona.apellido}`, cargo: persona.cargo_nombre || '' }]);
  };

  const setAprobador = (persona) => {
    setForm({ ...form, aprobador_nombre: `${persona.nombre} ${persona.apellido}`, aprobador_cargo: persona.cargo_nombre || '' });
  };

  const submit = async () => {
    if (!form.codigo) { toast.error('Ingrese un código'); return; }
    if (!form.fecha_evaluacion) { toast.error('Ingrese la fecha'); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/qra`, { ...form, evaluadores });
      toast.success('QRA creada exitosamente');
      onCreated(data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear');
    }
    setSaving(false);
  };

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Shield className="w-5 h-5 text-teal-500" /> Nueva Evaluación QRA
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Nombre de la QRA *</label>
            <input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Ej: Chancado, Molienda..."
              data-testid="qra-nombre" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Código Identificación *</label>
            <input value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="LB-IV-GPR-XXX-0001"
              data-testid="qra-codigo" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Fecha de Evaluación *</label>
            <input type="date" value={form.fecha_evaluacion} onChange={(e) => setForm({ ...form, fecha_evaluacion: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="qra-fecha" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Gerencia</label>
            <select value={form.gerencia_id} onChange={(e) => setForm({ ...form, gerencia_id: e.target.value, superintendencia_id: '' })}
              className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="qra-gerencia">
              <option value="">Seleccione...</option>
              {gerencias.map((g) => <option key={g.id} value={g.id}>{g.nombre}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Superintendencia</label>
            <select value={form.superintendencia_id} onChange={(e) => setForm({ ...form, superintendencia_id: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="qra-super">
              <option value="">Seleccione...</option>
              {filteredSupers.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Área</label>
            <select value={form.area_id} onChange={(e) => setForm({ ...form, area_id: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="qra-area">
              <option value="">Seleccione...</option>
              {areas.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-slate-600 mb-1 block">Equipo Evaluador</label>
          <PersonSearch onSelect={addEvaluador} placeholder="Buscar persona para agregar al equipo..." />
          {evaluadores.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {evaluadores.map((ev, i) => (
                <span key={i} className="bg-teal-50 text-teal-700 text-xs px-2 py-1 rounded-full flex items-center gap-1"
                  data-testid={`evaluador-${i}`}>
                  {ev.nombre} <span className="text-teal-400">({ev.cargo})</span>
                  <button onClick={() => setEvaluadores(evaluadores.filter((_, j) => j !== i))} className="ml-1 hover:text-red-500">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="text-xs font-medium text-slate-600 mb-1 block">Aprobador</label>
          <PersonSearch onSelect={setAprobador} placeholder="Buscar aprobador..." />
          {form.aprobador_nombre && (
            <p className="text-sm text-teal-600 mt-1">
              {form.aprobador_nombre} <span className="text-slate-400">({form.aprobador_cargo})</span>
            </p>
          )}
        </div>

        <div className="flex gap-3 pt-2">
          <button onClick={onCancel} className="px-4 py-2 border rounded-lg text-sm hover:bg-slate-50">Cancelar</button>
          <button onClick={submit} disabled={saving} data-testid="qra-submit"
            className="px-4 py-2 bg-teal-500 text-white rounded-lg text-sm hover:bg-teal-600 disabled:opacity-50">
            {saving ? 'Creando...' : 'Crear QRA'}
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

function EditQRAForm({ qra, onSaved, onCancel }) {
  const [gerencias, setGerencias] = useState([]);
  const [supers, setSupers] = useState([]);
  const [areas, setAreas] = useState([]);
  const [evaluadores, setEvaluadores] = useState([]);
  const [form, setForm] = useState({
    nombre: qra?.nombre || '', codigo: qra?.codigo || '',
    gerencia_id: qra?.gerencia_id || '', superintendencia_id: qra?.superintendencia_id || '',
    area_id: qra?.area_id || '', fecha_evaluacion: qra?.fecha_evaluacion || '',
    aprobador_nombre: qra?.aprobador_nombre || '', aprobador_cargo: qra?.aprobador_cargo || '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/gerencias`),
      axios.get(`${API}/superintendencias`),
      axios.get(`${API}/areas`),
      axios.get(`${API}/qra/${qra.id}`),
    ]).then(([g, s, a, detail]) => {
      setGerencias(g.data); setSupers(s.data); setAreas(a.data);
      if (detail.data.evaluadores) {
        setEvaluadores(detail.data.evaluadores.map((ev) => ({
          persona_id: ev.persona_id, nombre: ev.nombre, cargo: ev.cargo
        })));
      }
      // Convert DD-MM-YYYY back to YYYY-MM-DD for date input
      const fecha = detail.data.fecha_evaluacion;
      if (fecha && fecha.includes('-') && fecha.length === 10) {
        const parts = fecha.split('-');
        if (parts[0].length === 2) {
          setForm((f) => ({ ...f, fecha_evaluacion: `${parts[2]}-${parts[1]}-${parts[0]}` }));
        }
      }
    });
  }, [qra.id]);

  const filteredSupers = form.gerencia_id ? supers.filter((s) => s.gerencia_id === form.gerencia_id) : supers;

  const addEvaluador = (persona) => {
    if (evaluadores.find((e) => e.persona_id === persona.id)) return;
    setEvaluadores([...evaluadores, { persona_id: persona.id, nombre: `${persona.nombre} ${persona.apellido}`, cargo: persona.cargo_nombre || '' }]);
  };

  const setAprobador = (persona) => {
    setForm({ ...form, aprobador_nombre: `${persona.nombre} ${persona.apellido}`, aprobador_cargo: persona.cargo_nombre || '' });
  };

  const submit = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/qra/${qra.id}`, { ...form, evaluadores });
      toast.success('QRA actualizada');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al actualizar'); }
    setSaving(false);
  };

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Pencil className="w-5 h-5 text-teal-500" /> Editar QRA
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Nombre de la QRA</label>
            <input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-qra-nombre" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Código</label>
            <input value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-qra-codigo" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Fecha de Evaluación</label>
            <input type="date" value={form.fecha_evaluacion} onChange={(e) => setForm({ ...form, fecha_evaluacion: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-qra-fecha" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Gerencia</label>
            <select value={form.gerencia_id} onChange={(e) => setForm({ ...form, gerencia_id: e.target.value, superintendencia_id: '' })}
              className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-qra-gerencia">
              <option value="">Seleccione...</option>
              {gerencias.map((g) => <option key={g.id} value={g.id}>{g.nombre}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Superintendencia</label>
            <select value={form.superintendencia_id} onChange={(e) => setForm({ ...form, superintendencia_id: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-qra-super">
              <option value="">Seleccione...</option>
              {filteredSupers.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Área</label>
            <select value={form.area_id} onChange={(e) => setForm({ ...form, area_id: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-qra-area">
              <option value="">Seleccione...</option>
              {areas.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1 block">Equipo Evaluador</label>
          <PersonSearch onSelect={addEvaluador} placeholder="Buscar persona para agregar al equipo..." />
          {evaluadores.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {evaluadores.map((ev, i) => (
                <span key={i} className="bg-teal-50 text-teal-700 text-xs px-2 py-1 rounded-full flex items-center gap-1"
                  data-testid={`edit-evaluador-${i}`}>
                  {ev.nombre} <span className="text-teal-400">({ev.cargo})</span>
                  <button onClick={() => setEvaluadores(evaluadores.filter((_, j) => j !== i))} className="ml-1 hover:text-red-500">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1 block">Aprobador</label>
          <PersonSearch onSelect={setAprobador} placeholder="Buscar aprobador..." />
          {form.aprobador_nombre && (
            <p className="text-sm text-teal-600 mt-1">{form.aprobador_nombre} <span className="text-slate-400">({form.aprobador_cargo})</span></p>
          )}
        </div>
        <div className="flex gap-3 pt-2">
          <button onClick={onCancel} className="px-4 py-2 border rounded-lg text-sm hover:bg-slate-50">Cancelar</button>
          <button onClick={submit} disabled={saving} data-testid="edit-qra-submit"
            className="px-4 py-2 bg-teal-500 text-white rounded-lg text-sm hover:bg-teal-600 disabled:opacity-50">
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

function QRADetail({ qraId, onBack }) {
  const [qra, setQra] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [warnings, setWarnings] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/qra/${qraId}`);
      setQra(data);
    } catch { toast.error('Error al cargar QRA'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [qraId]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadFile(file);
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/qra/${qraId}/upload`, fd);
      if (data.status === 'warnings') {
        setWarnings(data.warnings);
      } else {
        toast.success(`${data.filas_importadas} filas importadas`);
        load();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cargar');
    }
    setUploading(false);
  };

  const forceUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', uploadFile);
    try {
      const { data } = await axios.post(`${API}/qra/${qraId}/upload-force`, fd);
      toast.success(`${data.filas_importadas} filas importadas (forzado)`);
      setWarnings(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
    setUploading(false);
  };

  if (loading) return <p className="text-center py-10 text-slate-400">Cargando...</p>;
  if (!qra) return <p className="text-center py-10 text-red-400">QRA no encontrada</p>;

  const filas = qra.filas || [];
  const kpis = qra.kpis || {};

  return (
    <div className="space-y-4">
      {warnings && <ValidationModal warnings={warnings} onForce={forceUpload} onCancel={() => setWarnings(null)} />}

      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-700" data-testid="qra-back">Volver al listado</button>
        <div className="flex gap-2">
          <input type="file" ref={fileRef} accept=".xlsx,.xls" className="hidden" onChange={handleUpload} />
          <button onClick={() => fileRef.current.click()} disabled={uploading} data-testid="qra-upload-btn"
            className="flex items-center gap-2 px-3 py-2 bg-teal-500 text-white rounded-lg text-sm hover:bg-teal-600 disabled:opacity-50">
            <Upload className="w-4 h-4" /> {uploading ? 'Cargando...' : 'Cargar Excel QRA'}
          </button>
        </div>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h2 className="text-xl font-bold text-slate-900" data-testid="qra-detail-code">
                {qra.nombre ? `QRA ${qra.nombre}` : 'QRA'} <span className="text-base font-normal text-slate-400 ml-2">{qra.codigo}</span>
              </h2>
            </div>
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${qra.estado === 'activo' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
              {qra.estado === 'activo' ? 'Activo' : 'Borrador'}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm">
            <div><span className="text-slate-400 text-xs">Gerencia</span><p className="font-medium text-slate-700" data-testid="qra-gerencia-val">{qra.gerencia_nombre || '-'}</p></div>
            <div><span className="text-slate-400 text-xs">Superintendencia</span><p className="font-medium text-slate-700" data-testid="qra-super-val">{qra.superintendencia_nombre || '-'}</p></div>
            <div><span className="text-slate-400 text-xs">Área</span><p className="font-medium text-slate-700" data-testid="qra-area-val">{qra.area_nombre || '-'}</p></div>
            <div><span className="text-slate-400 text-xs">Fecha Evaluación</span><p className="font-medium text-slate-700">{qra.fecha_evaluacion || '-'}</p></div>
            <div><span className="text-slate-400 text-xs">Aprobador</span><p className="font-medium text-slate-700" data-testid="qra-aprobador-val">{qra.aprobador_nombre || '-'} {qra.aprobador_cargo ? `(${qra.aprobador_cargo})` : ''}</p></div>
            <div>
              <span className="text-slate-400 text-xs">Equipo Evaluador</span>
              <div className="flex flex-wrap gap-1 mt-0.5">
                {qra.evaluadores?.length > 0 ? qra.evaluadores.map((ev, i) => (
                  <span key={i} className="bg-teal-50 text-teal-700 text-xs px-2 py-0.5 rounded-full">{ev.nombre}</span>
                )) : <p className="text-slate-400 text-xs">Sin asignar</p>}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {filas.length > 0 ? (
        <>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            <Card className="border-0 shadow-sm bg-slate-50"><CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-slate-900" data-testid="kpi-total">{kpis.total_filas || filas.length}</p>
              <p className="text-[10px] text-slate-500 leading-tight">Total Filas</p>
            </CardContent></Card>
            <Card className="border-0 shadow-sm bg-amber-50"><CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-amber-700" data-testid="kpi-peligros">{kpis.peligros_unicos || 0}</p>
              <p className="text-[10px] text-slate-500 leading-tight">Peligros Únicos</p>
            </CardContent></Card>
            <Card className="border-0 shadow-sm bg-blue-50"><CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-blue-700" data-testid="kpi-actividades">{kpis.actividades_unicas || 0}</p>
              <p className="text-[10px] text-slate-500 leading-tight">Actividades</p>
            </CardContent></Card>
            <Card className="border-0 shadow-sm bg-red-50"><CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-red-600" data-testid="kpi-ri-medio">{kpis.ri_medio || 0}</p>
              <p className="text-[10px] text-slate-500 leading-tight">RI Medio</p>
            </CardContent></Card>
            <Card className="border-0 shadow-sm bg-green-50"><CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-green-600" data-testid="kpi-rr-bajo">{kpis.rr_bajo || 0}</p>
              <p className="text-[10px] text-slate-500 leading-tight">RR Bajo</p>
            </CardContent></Card>
            <Card className="border-0 shadow-sm bg-teal-50"><CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-teal-600" data-testid="kpi-controles">{kpis.controles_unicos || 0}</p>
              <p className="text-[10px] text-slate-500 leading-tight">Controles Únicos</p>
            </CardContent></Card>
          </div>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse" data-testid="qra-table">
                  <thead>
                    <tr className="bg-slate-800 text-white">
                      <th className="px-2 py-2 text-center w-[36px]">#</th>
                      <th className="px-2 py-2 text-center min-w-[120px]">Actividad</th>
                      <th className="px-2 py-2 text-center min-w-[70px] max-w-[100px]">Tarea</th>
                      <th className="px-2 py-2 text-center min-w-[110px]">Peligro</th>
                      <th className="px-2 py-2 text-center min-w-[110px]">Evento</th>
                      <th className="px-2 py-2 text-center w-[40px]">RI</th>
                      <th className="px-2 py-2 text-center min-w-[260px]">Controles</th>
                      <th className="px-2 py-2 text-center w-[50px]">Efect.</th>
                      <th className="px-2 py-2 text-center w-[40px]">RR</th>
                      <th className="px-2 py-2 text-center min-w-[100px]">Plan Acción</th>
                      <th className="px-2 py-2 text-center w-[55px]">Estado</th>
                      <th className="px-2 py-2 text-center w-[60px]">DS-44</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const rows = [];
                      const spans = {};
                      const groupCols = [
                        'actividad', 'tarea', 'peligro', 'evento',
                        'ri_calificacion', 'controles_descripcion',
                        'control_efectividad', 'rr_calificacion',
                        'plan_accion', 'plan_estado', 'ds44_riesgo'
                      ];
                      const ancestors = {
                        actividad: [],
                        tarea: ['actividad'],
                        peligro: ['actividad'],
                        evento: ['actividad', 'peligro'],
                        ri_calificacion: ['actividad', 'peligro', 'evento'],
                        controles_descripcion: ['actividad', 'peligro', 'evento'],
                        control_efectividad: ['actividad', 'peligro', 'evento', 'controles_descripcion'],
                        rr_calificacion: ['actividad', 'peligro', 'evento', 'controles_descripcion'],
                        plan_accion: ['actividad', 'peligro', 'evento', 'controles_descripcion'],
                        plan_estado: ['actividad', 'peligro', 'evento', 'controles_descripcion'],
                        ds44_riesgo: ['actividad', 'peligro', 'evento', 'controles_descripcion'],
                      };
                      const gk = (row, col) => [String(row[col] ?? ''), ...ancestors[col].map((a) => String(row[a] ?? ''))].join('|||');
                      for (let i = 0; i < filas.length; i++) {
                        groupCols.forEach((col) => {
                          const key = col + '_' + i;
                          const myKey = gk(filas[i], col);
                          if (i === 0 || myKey !== gk(filas[i - 1], col)) {
                            let span = 1;
                            for (let j = i + 1; j < filas.length; j++) {
                              if (gk(filas[j], col) !== myKey) break;
                              span++;
                            }
                            spans[key] = span;
                          } else {
                            spans[key] = 0;
                          }
                        });
                      }
                      const S = (col, i) => spans[col + '_' + i];
                      filas.forEach((f, i) => {
                        const isNewAct = S('actividad', i) > 0 && i > 0;
                        const isNewTar = S('tarea', i) > 0 && i > 0 && S('actividad', i) === 0;
                        rows.push(
                          <tr key={f.id} className={`border-b ${isNewAct ? 'border-t-2 border-t-slate-400' : isNewTar ? 'border-t border-t-slate-300' : 'border-slate-200'}`}>
                            <td className="px-2 py-1.5 text-center align-middle text-slate-400 border-r border-slate-100">{f.fila_num}</td>
                            {S('actividad', i) > 0 && <td rowSpan={S('actividad', i)} className="px-2 py-2 text-center align-middle font-semibold text-slate-800 bg-teal-50/60 border-r-2 border-r-teal-200">{f.actividad}</td>}
                            {S('tarea', i) > 0 && <td rowSpan={S('tarea', i)} className="px-2 py-2 text-center align-middle text-slate-700 bg-slate-50 border-r border-slate-200 max-w-[100px]">{f.tarea}</td>}
                            {S('peligro', i) > 0 && <td rowSpan={S('peligro', i)} className="px-2 py-2 text-center align-middle text-slate-600 border-r border-slate-100">{f.peligro}</td>}
                            {S('evento', i) > 0 && <td rowSpan={S('evento', i)} className="px-2 py-1.5 text-center align-middle">{f.evento}</td>}
                            {S('ri_calificacion', i) > 0 && <td rowSpan={S('ri_calificacion', i)} className="px-2 py-1.5 text-center align-middle"><RiskBadge value={f.ri_calificacion} color={f.ri_color} /></td>}
                            {S('controles_descripcion', i) > 0 && <td rowSpan={S('controles_descripcion', i)} className="px-2 py-1.5 text-left align-middle"><div className="text-xs leading-tight">{f.controles_descripcion}</div></td>}
                            {S('control_efectividad', i) > 0 && <td rowSpan={S('control_efectividad', i)} className="px-2 py-1.5 text-center align-middle text-[10px]">{f.control_efectividad}</td>}
                            {S('rr_calificacion', i) > 0 && <td rowSpan={S('rr_calificacion', i)} className="px-2 py-1.5 text-center align-middle"><RiskBadge value={f.rr_calificacion} color={f.rr_color} /></td>}
                            {S('plan_accion', i) > 0 && <td rowSpan={S('plan_accion', i)} className="px-2 py-1.5 text-center align-middle" title={f.plan_accion}>{f.plan_accion}</td>}
                            {S('plan_estado', i) > 0 && <td rowSpan={S('plan_estado', i)} className="px-2 py-1.5 text-center align-middle">
                              {f.plan_estado === 'Cerrada' ? <CheckCircle className="w-4 h-4 text-green-500 mx-auto" /> :
                               f.plan_estado ? <span className="text-[10px] text-amber-600">{f.plan_estado}</span> : '-'}
                            </td>}
                            {S('ds44_riesgo', i) > 0 && <td rowSpan={S('ds44_riesgo', i)} className="px-2 py-1.5 text-center align-middle text-[10px]">{f.ds44_riesgo || '-'}</td>}
                          </tr>
                        );
                      });
                      return rows;
                    })()}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-10 text-center">
            <FileSpreadsheet className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">No hay datos cargados aún</p>
            <p className="text-slate-400 text-xs mt-1">Suba un archivo Excel QRA para importar las filas de evaluación</p>
            <button onClick={() => fileRef.current.click()} data-testid="qra-upload-empty"
              className="mt-4 px-4 py-2 bg-teal-500 text-white rounded-lg text-sm hover:bg-teal-600">
              <Upload className="w-4 h-4 inline mr-2" />Cargar Excel QRA
            </button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function QRA() {
  const [qras, setQras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [selectedId, setSelectedId] = useState(null);
  const [gerencias, setGerencias] = useState([]);
  const [filtroGer, setFiltroGer] = useState('');
  const [filtroSup, setFiltroSup] = useState('');
  const [supers, setSupers] = useState([]);
  const [confirmAction, setConfirmAction] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/qra`);
      setQras(data);
    } catch { /* */ }
    setLoading(false);
  };

  useEffect(() => {
    load();
    Promise.all([axios.get(`${API}/gerencias`), axios.get(`${API}/superintendencias`)])
      .then(([g, s]) => { setGerencias(g.data); setSupers(s.data); });
  }, []);

  const downloadTemplate = async () => {
    try {
      const resp = await axios.get(`${API}/qra/plantilla/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Plantilla_QRA.xlsx';
      a.click();
      toast.success('Plantilla descargada');
    } catch {
      toast.error('Error al descargar plantilla');
    }
  };

  const deleteQRA = async (id) => {
    setConfirmAction({
      message: '¿Eliminar esta QRA? Esta accion no se puede deshacer.',
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/qra/${id}`);
          toast.success('QRA eliminada');
          load();
        } catch { toast.error('Error al eliminar'); }
        setConfirmAction(null);
      }
    });
  };

  if (view === 'create') {
    return (
      <div className="max-w-3xl mx-auto" data-testid="qra-page">
        <CreateQRAForm
          onCreated={(id) => { setSelectedId(id); setView('detail'); load(); }}
          onCancel={() => setView('list')} />
      </div>
    );
  }

  if (view === 'edit' && selectedId) {
    const editQra = qras.find((q) => q.id === selectedId);
    return (
      <div className="max-w-3xl mx-auto" data-testid="qra-page">
        <EditQRAForm qra={editQra} onSaved={() => { setView('list'); load(); }} onCancel={() => setView('list')} />
      </div>
    );
  }

  if (view === 'detail' && selectedId) {
    return (
      <div data-testid="qra-page">
        <QRADetail qraId={selectedId} onBack={() => { setView('list'); load(); }} />
      </div>
    );
  }

  const filteredSupers = filtroGer ? supers.filter((s) => s.gerencia_id === filtroGer) : supers;
  const filteredQras = qras.filter((q) => {
    if (filtroGer && q.gerencia_id !== filtroGer) return false;
    if (filtroSup && q.superintendencia_id !== filtroSup) return false;
    return true;
  });

  return (
    <div className="space-y-4" data-testid="qra-page">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-900">QRA - Evaluación Cualitativa de Riesgos</h1>
        <div className="flex gap-2">
          <button onClick={downloadTemplate} data-testid="qra-download-template"
            className="flex items-center gap-2 px-3 py-2 border rounded-lg text-sm hover:bg-slate-50">
            <Download className="w-4 h-4" /> Plantilla QRA
          </button>
          <button onClick={() => setView('create')} data-testid="qra-new"
            className="flex items-center gap-2 px-3 py-2 bg-teal-500 text-white rounded-lg text-sm hover:bg-teal-600">
            <Plus className="w-4 h-4" /> Nueva QRA
          </button>
        </div>
      </div>

      <div className="flex gap-3 items-center">
        <select value={filtroGer} onChange={(e) => { setFiltroGer(e.target.value); setFiltroSup(''); }}
          className="border rounded-lg px-3 py-2 text-sm min-w-[180px]" data-testid="qra-filter-gerencia">
          <option value="">Todas las Gerencias</option>
          {gerencias.map((g) => <option key={g.id} value={g.id}>{g.nombre}</option>)}
        </select>
        <select value={filtroSup} onChange={(e) => setFiltroSup(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm min-w-[200px]" data-testid="qra-filter-super">
          <option value="">Todas las Superintendencias</option>
          {filteredSupers.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
        </select>
        {(filtroGer || filtroSup) && (
          <button onClick={() => { setFiltroGer(''); setFiltroSup(''); }}
            className="text-xs text-slate-500 hover:text-red-500">Limpiar filtros</button>
        )}
        <span className="text-xs text-slate-400 ml-auto">{filteredQras.length} de {qras.length} evaluaciones</span>
      </div>

      {loading ? (
        <p className="text-center py-10 text-slate-400">Cargando...</p>
      ) : filteredQras.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-10 text-center">
            <Shield className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">{qras.length === 0 ? 'No hay evaluaciones QRA registradas' : 'No hay QRA que coincidan con los filtros'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {filteredQras.map((q) => (
            <Card key={q.id} className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => { setSelectedId(q.id); setView('detail'); }} data-testid={`qra-card-${q.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <Shield className="w-5 h-5 text-teal-500 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-slate-900">{q.nombre ? `QRA ${q.nombre}` : q.codigo || 'Sin código'}</p>
                        {q.nombre && <span className="text-xs text-slate-400">{q.codigo}</span>}
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                          q.estado === 'activo' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                        }`}>{q.estado}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{q.gerencia_nombre || '-'} / {q.superintendencia_nombre || '-'} / {q.area_nombre || '-'}</p>
                      <div className="flex items-center gap-4 mt-2 text-[11px] text-slate-500">
                        {q.aprobador_nombre && <span>Aprobador: <strong className="text-slate-700">{q.aprobador_nombre}</strong></span>}
                        {q.fecha_evaluacion && <span>Fecha: <strong className="text-slate-700">{q.fecha_evaluacion}</strong></span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 ml-4">
                    <div className="flex gap-3 text-center">
                      <div><p className="text-lg font-bold text-amber-600">{q.tareas_unicas || 0}</p><p className="text-[10px] text-slate-400">Tareas</p></div>
                      <div><p className="text-lg font-bold text-orange-500">{q.peligros_unicos || 0}</p><p className="text-[10px] text-slate-400">Peligros</p></div>
                      <div><p className="text-lg font-bold text-teal-600">{q.controles_unicos || 0}</p><p className="text-[10px] text-slate-400">Controles</p></div>
                    </div>
                    <div className="flex items-center gap-1 border-l pl-3 ml-1">
                      <button onClick={(e) => { e.stopPropagation(); setSelectedId(q.id); setView('edit'); }}
                        className="p-1.5 text-slate-400 hover:text-teal-600 rounded hover:bg-slate-50" data-testid={`qra-edit-${q.id}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); deleteQRA(q.id); }}
                        className="p-1.5 text-slate-400 hover:text-red-500 rounded hover:bg-slate-50" data-testid={`qra-delete-${q.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {confirmAction && createPortal(
        <div style={{backgroundColor:"rgba(0,0,0,0.5)"}} className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="confirm-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <p className="text-sm text-slate-700">{confirmAction.message}</p>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmAction(null)}
                className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                data-testid="confirm-cancel-btn">Cancelar</button>
              <button onClick={confirmAction.onConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                data-testid="confirm-ok-btn">Confirmar</button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}
